import { cardFullscreen } from "../../../shared/cards/CardFullscreen";
import { createVerseCardFace } from "../../../shared/cards/cardFace";
import {
  ownedCount,
  type CardCounts,
} from "../campaign";
import { verseById } from "../data/verses";
import { keywordsForVerse } from "../game/cards/keywords";
import type { VerseDef } from "../game/cards/types";

function openVerseStudy(def: VerseDef): void {
  const speak = def.speakSeconds ?? 2.5;
  const kws = keywordsForVerse(def);
  const detailRows: { label: string; value: string }[] = [
    { label: "Speak", value: `${speak.toFixed(1)}s` },
    { label: "Kind", value: def.kind },
    { label: "Category", value: def.category },
  ];
  if (def.strongVs?.length) {
    detailRows.push({
      label: "Meets",
      value: def.strongVs
        .map((t) => t.charAt(0).toUpperCase() + t.slice(1))
        .join(" / "),
    });
  }
  if (def.preferLayer) {
    detailRows.push({ label: "Layer", value: def.preferLayer });
  }
  if (def.lockLayer) {
    detailRows.push({ label: "Lock", value: def.lockLayer });
  }
  cardFullscreen.show({
    verseId: def.id,
    reference: def.reference,
    category: def.category,
    speakSeconds: speak,
    metaLine: `${def.category} · ${def.kind}`,
    keywords: kws.map((k) => ({
      id: k.id,
      label: k.label,
      reminder: k.reminder,
    })),
    textKjv: def.textKjv,
    proclaimText: def.proclaimText,
    effectSummary: def.effectSummary,
    detailRows,
    studyLayout: true,
  });
}

export class RewardPicker {
  private root: HTMLElement;
  private titleEl: HTMLElement;
  private leadEl: HTMLElement;
  private optionsEl: HTMLElement;
  private skipBtn: HTMLElement | null = null;
  private onPick: (verseId: string) => void;
  private onSkip: () => void;
  private allowed: string[] = [];
  private mode: "pick" | "auto" = "pick";

  constructor(
    root: HTMLElement,
    handlers: {
      onPick: (verseId: string) => void;
      onSkip: () => void;
    },
  ) {
    this.root = root;
    this.onPick = handlers.onPick;
    this.onSkip = handlers.onSkip;

    this.root.classList.add("reward-overlay");
    this.root.innerHTML = `
      <div class="overlay-card reward-card" role="document">
        <h2 data-title>Verse earned</h2>
        <p class="campaign-lead" data-lead>Choose one for your arsenal.</p>
        <div class="reward-options reward-card-grid" data-options></div>
        <div class="modal-actions end-actions reward-actions">
          <button type="button" class="btn secondary" data-act="skip" title="Decline the verse pick; the stage still counts as cleared">Skip reward (still count as clear)</button>
        </div>
      </div>
    `;

    this.titleEl = this.root.querySelector("[data-title]")!;
    this.leadEl = this.root.querySelector("[data-lead]")!;
    this.optionsEl = this.root.querySelector("[data-options]")!;
    this.skipBtn = this.root.querySelector("[data-act='skip']");

    this.root.addEventListener("click", (e) => {
      const t = e.target as HTMLElement;

      const study = t.closest("[data-study]") as HTMLElement | null;
      if (study?.dataset.study) {
        e.preventDefault();
        e.stopPropagation();
        const def = verseById(study.dataset.study);
        if (def) openVerseStudy(def);
        return;
      }

      const pick = t.closest("[data-pick]") as HTMLElement | null;
      if (pick?.dataset.pick && this.mode === "pick") {
        const id = pick.dataset.pick;
        if (this.allowed.includes(id)) this.onPick(id);
        return;
      }

      if (t.closest("[data-act='skip']") || t.closest("[data-act='continue']")) {
        this.onSkip();
      }
    });
  }

  showPick(
    options: string[],
    opts?: {
      title?: string;
      lead?: string;
      /** Copies owned *before* this pick (so New vs Owned is clear). */
      ownedCounts?: CardCounts;
      /** Auto-granted this victory (e.g. chapter signature). */
      alsoGranted?: string[];
      skipLabel?: string;
      /** When false, hide skip (mandatory pick). Default true. */
      allowSkip?: boolean;
      /** When false, omit the default “stacks” line (custom lead only). */
      appendPickHint?: boolean;
      /**
       * Field-deck pick (Muster): no arsenal New/Owned chrome; whole tile is
       * the choice (study via long-press is not needed mid-fight).
       */
      fieldPick?: boolean;
    },
  ): void {
    this.mode = "pick";
    this.allowed = [...options];
    this.titleEl.textContent = opts?.title ?? "Choose a verse";
    const parts: string[] = [];
    if (opts?.lead) parts.push(opts.lead);
    if (opts?.alsoGranted?.length) {
      parts.push("Chapter signature added to your collection.");
    }
    if (opts?.appendPickHint !== false) {
      parts.push(
        "Study a card, then choose one for +1 copy (stacks if you already own it).",
      );
    }
    this.leadEl.textContent = parts.join(" ");
    this.optionsEl.className = "reward-options reward-card-grid";
    this.optionsEl.replaceChildren();

    const owned = opts?.ownedCounts ?? {};
    for (const id of opts?.alsoGranted ?? []) {
      this.optionsEl.appendChild(
        this.makeRewardTile(id, {
          mode: "auto",
          ownedNow: ownedCount(owned, id),
        }),
      );
    }
    for (const id of options) {
      if (opts?.fieldPick) {
        this.optionsEl.appendChild(this.makeFieldPickTile(id));
      } else {
        this.optionsEl.appendChild(
          this.makeRewardTile(id, {
            mode: "pick",
            ownedBefore: ownedCount(owned, id),
          }),
        );
      }
    }

    if (this.skipBtn) {
      if (opts?.allowSkip === false) {
        this.skipBtn.style.display = "none";
      } else {
        this.skipBtn.style.display = "";
        this.skipBtn.textContent =
          opts?.skipLabel ?? "Skip reward (still count as clear)";
        this.skipBtn.className = "btn secondary";
        this.skipBtn.setAttribute("data-act", "skip");
        this.skipBtn.title =
          opts?.skipLabel ??
          "Decline the verse pick; the stage still counts as cleared";
      }
    }
    this.root.classList.remove("hidden");
  }

  showAuto(
    granted: string[],
    opts?: {
      title?: string;
      lead?: string;
      /** Owned counts *after* grant (current arsenal). */
      ownedCounts?: CardCounts;
      continueLabel?: string;
    },
  ): void {
    this.mode = "auto";
    this.allowed = [];
    this.titleEl.textContent = opts?.title ?? "Hard clear — full set";
    this.leadEl.textContent =
      opts?.lead ??
      (granted.length > 1
        ? "+1 copy of each reward added to your arsenal. Tap a card to study."
        : "+1 copy added to your arsenal. Tap a card to study.");
    this.optionsEl.className = "reward-options reward-card-grid";
    this.optionsEl.replaceChildren();

    const owned = opts?.ownedCounts ?? {};
    for (const id of granted) {
      this.optionsEl.appendChild(
        this.makeRewardTile(id, {
          mode: "auto",
          ownedNow: ownedCount(owned, id),
        }),
      );
    }

    if (this.skipBtn) {
      this.skipBtn.style.display = "";
      this.skipBtn.textContent = opts?.continueLabel ?? "Continue";
      this.skipBtn.className = "btn primary";
      this.skipBtn.setAttribute("data-act", "continue");
    }
    this.root.classList.remove("hidden");
  }

  /** Text-only notice (e.g. gallery keepsake with no verse tiles). */
  showNotice(opts: {
    title: string;
    lead: string;
    continueLabel?: string;
  }): void {
    this.mode = "auto";
    this.allowed = [];
    this.titleEl.textContent = opts.title;
    this.leadEl.textContent = opts.lead;
    this.optionsEl.className = "reward-options reward-card-grid";
    this.optionsEl.replaceChildren();
    if (this.skipBtn) {
      this.skipBtn.style.display = "";
      this.skipBtn.textContent = opts.continueLabel ?? "Continue";
      this.skipBtn.className = "btn primary";
      this.skipBtn.setAttribute("data-act", "continue");
      this.skipBtn.title = "";
    }
    this.root.classList.remove("hidden");
  }

  hide(): void {
    this.root.classList.add("hidden");
    if (this.skipBtn) {
      this.skipBtn.style.display = "";
      this.skipBtn.textContent = "Skip reward (still count as clear)";
      this.skipBtn.className = "btn secondary";
      this.skipBtn.setAttribute("data-act", "skip");
      this.skipBtn.title =
        "Decline the verse pick; the stage still counts as cleared";
    }
  }

  isOpen(): boolean {
    return !this.root.classList.contains("hidden");
  }

  private makeRewardTile(
    verseId: string,
    opts:
      | { mode: "pick"; ownedBefore: number }
      | { mode: "auto"; ownedNow: number },
  ): HTMLElement {
    const def = verseById(verseId);
    const tile = document.createElement("div");
    tile.className = "reward-tile";
    tile.dataset.verseId = verseId;

    const owned =
      opts.mode === "pick" ? opts.ownedBefore : Math.max(0, opts.ownedNow - 1);
    const now =
      opts.mode === "pick" ? opts.ownedBefore + 1 : opts.ownedNow;
    const isNew = opts.mode === "pick" ? opts.ownedBefore === 0 : owned === 0;

    if (opts.mode === "pick" && owned > 0) {
      tile.classList.add("is-owned");
    }
    if (isNew) {
      tile.classList.add("is-new");
    }

    // Top-left status: New vs Owned (qty lives on the card corner when > 1)
    const badge = document.createElement("span");
    badge.className = "reward-owned-badge" + (isNew ? " is-new" : " is-have");
    if (opts.mode === "pick") {
      badge.textContent = opts.ownedBefore === 0 ? "New" : "Owned";
      badge.title =
        opts.ownedBefore === 0
          ? "You do not own this verse yet"
          : `You already have ${opts.ownedBefore} cop${opts.ownedBefore === 1 ? "y" : "ies"} — pick adds +1 (→ ×${now})`;
    } else {
      badge.textContent = "Granted";
      badge.title = `You now have ${opts.ownedNow} cop${opts.ownedNow === 1 ? "y" : "ies"} of this verse`;
      badge.classList.add("is-have");
      badge.classList.remove("is-new");
    }
    tile.appendChild(badge);

    // Card face — study on click
    const faceBtn = document.createElement("button");
    faceBtn.type = "button";
    faceBtn.className = "reward-face-btn";
    faceBtn.dataset.study = verseId;
    faceBtn.title = def
      ? `Study ${def.reference}`
      : `Study ${verseId}`;
    faceBtn.setAttribute(
      "aria-label",
      def ? `Study ${def.reference}` : `Study ${verseId}`,
    );

    // Lower-right copy count when you already have more than one
    const copiesOnCard =
      opts.mode === "pick" ? opts.ownedBefore : opts.ownedNow;

    if (def) {
      const face = createVerseCardFace({
        verseId: def.id,
        reference: def.reference,
        category: def.category,
        speakSeconds: def.speakSeconds ?? 2.5,
        metaLine: `${def.category} · ${def.kind}`,
        size: "static",
      });
      face.classList.add("reward-face");
      face.setAttribute("aria-hidden", "true");
      // Badge lives on the card (position:relative) so bottom-right is the card corner
      if (copiesOnCard > 1) {
        const qty = document.createElement("span");
        qty.className = "reward-card-qty";
        qty.textContent = `×${copiesOnCard}`;
        qty.setAttribute("aria-hidden", "true");
        qty.title =
          opts.mode === "pick"
            ? `${copiesOnCard} copies in arsenal`
            : `Now ${copiesOnCard} copies in arsenal`;
        face.appendChild(qty);
      }
      faceBtn.appendChild(face);
    } else {
      const fallback = document.createElement("span");
      fallback.className = "reward-face-fallback";
      fallback.textContent = verseId;
      faceBtn.appendChild(fallback);
      if (copiesOnCard > 1) {
        const qty = document.createElement("span");
        qty.className = "reward-card-qty";
        qty.textContent = `×${copiesOnCard}`;
        qty.setAttribute("aria-hidden", "true");
        faceBtn.appendChild(qty);
      }
    }

    const studyHint = document.createElement("span");
    studyHint.className = "reward-study-hint";
    studyHint.textContent = "Study";
    faceBtn.appendChild(studyHint);
    tile.appendChild(faceBtn);

    // Status line under card
    const status = document.createElement("p");
    status.className = "reward-status";
    if (opts.mode === "pick") {
      if (opts.ownedBefore === 0) {
        status.innerHTML = `<span class="reward-status-new">Not in arsenal</span> · pick → ×1`;
      } else {
        status.innerHTML = `In arsenal <strong>×${opts.ownedBefore}</strong> · pick → <strong>×${now}</strong>`;
      }
    } else {
      status.innerHTML = `In arsenal <strong>×${opts.ownedNow}</strong>`;
    }
    tile.appendChild(status);

    if (opts.mode === "pick") {
      const pickBtn = document.createElement("button");
      pickBtn.type = "button";
      pickBtn.className = "btn primary reward-pick-btn";
      pickBtn.dataset.pick = verseId;
      pickBtn.textContent = isNew ? "Take this verse" : "Take +1 copy";
      tile.appendChild(pickBtn);
    }

    return tile;
  }

  /** Muster field provision / cull — pick is the only action (no arsenal chrome). */
  private makeFieldPickTile(verseId: string): HTMLElement {
    const def = verseById(verseId);
    const tile = document.createElement("div");
    tile.className = "reward-tile is-new";
    tile.dataset.verseId = verseId;

    if (def) {
      const face = createVerseCardFace({
        verseId: def.id,
        reference: def.reference,
        category: def.category,
        speakSeconds: def.speakSeconds ?? 2.5,
        metaLine: `${def.category} · ${def.kind}`,
        size: "static",
      });
      face.classList.add("reward-face");
      face.setAttribute("aria-hidden", "true");
      tile.appendChild(face);
    } else {
      const fallback = document.createElement("span");
      fallback.className = "reward-face-fallback";
      fallback.textContent = verseId;
      tile.appendChild(fallback);
    }

    const pickBtn = document.createElement("button");
    pickBtn.type = "button";
    pickBtn.className = "btn primary reward-pick-btn";
    pickBtn.dataset.pick = verseId;
    pickBtn.textContent = "Choose";
    pickBtn.setAttribute(
      "aria-label",
      def ? `Choose ${def.reference}` : `Choose ${verseId}`,
    );
    tile.appendChild(pickBtn);
    return tile;
  }
}
